import React, { useEffect } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import ProjectAPI from "../../../../api/ProjectAPI";
import Input from "../../../../components/form/Input";
import FormTranslations from "../../../../components/formTranslations/FormTranslations";
import FormTranslationsHeader from "../../../../components/formTranslations/FormTranslationsHeader";
import { FormProviderWithTranslations } from "../../../../contexts/FormTranslationsContext";

import type { JSX } from "react";
import type { UseFormReturn } from "react-hook-form";

import type {
  Platform,
  ProjectPageTranslation,
} from "../../../../api/ProjectAPI.types";
import type { PageFieldArray } from "../../../../contexts/FormTranslationsContext";
import type { AppState } from "../../../../reducers";

interface PageTranslationsProps {
  methods: UseFormReturn<
    PageFieldArray<ProjectPageTranslation>,
    unknown,
    PageFieldArray<ProjectPageTranslation>
  >;
}

export default function PageTranslations({
  methods,
}: PageTranslationsProps): JSX.Element | null {
  const { t } = useTranslation("editor-platform");

  const projectList = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );

  const { projectSlug, pageId } = useParams<{
    projectSlug: string;
    pageId: string;
    platform: Platform;
  }>();

  const projectTranslationList = projectList.data.find(
    (project) => project.name === projectSlug,
  )?.translations;

  const projectLanguageList = projectTranslationList?.map(
    (translation) => translation.language,
  );

  const projectDefaultLanguage = projectTranslationList?.find(
    (translation) => translation.default,
  )?.language;

  const isNew = pageId === "new";

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
    options: {
      enabled: !isNew,
    },
  });

  const { data: projectPageData } = useQuery(getProjectPageQuery);

  useEffect(() => {
    if (projectPageData) {
      methods.reset({
        translations: [...projectPageData.translations].sort((a, b) => {
          if (a.default) return -1;
          if (b.default) return 1;
          return a.language.localeCompare(b.language);
        }),
      });
    } else if (isNew) {
      methods.reset({
        translations: [
          {
            slug: "new-page",
            title: t("pages.new"),
            language: projectDefaultLanguage ?? "fr",
            default: true,
          },
        ],
      });
    }
  }, [projectPageData, isNew, methods, projectDefaultLanguage, t]);

  const handleAddLanguage = () => ({
    slug: "",
    title: "",
    default: false,
  });

  return (
    <FormProviderWithTranslations
      methods={methods}
      onAddLanguage={handleAddLanguage}
    >
      <div className="bg-card mb-[0.9375rem] rounded-[0.234375rem] p-[0.9375rem] drop-shadow-sm">
        <FormTranslationsHeader languageList={projectLanguageList} />
        <div className="flex flex-col gap-[1.5625rem] lg:flex-row">
          <div className="flex flex-1 flex-col">
            <FormTranslations slugController="title">
              <Input
                isCompact
                isRequired
                label={t("pages.slug")}
                name="slug"
                type="text"
              />
            </FormTranslations>
          </div>
          <div className="border-border mx-[0.3125rem] border-t lg:my-[0.3125rem] lg:border-l" />
          <div className="flex flex-1 flex-col">
            <FormTranslations>
              <Input
                isCompact
                isRequired
                label={t("pages.name")}
                name="title"
                type="text"
              />
            </FormTranslations>
          </div>
        </div>
      </div>
    </FormProviderWithTranslations>
  );
}
